<template>
  <div>
    <v-overlay v-model="isPending" color="#F2F2F2" opacity="1" z-index="7">
      <div class="d-flex align-center justify-center flex-column">
        <img src="@/assets/image/logo_loading.gif" width="120" />
        <div class="black--text text-center">
          <h3 class="mt-4" v-html="pendingWording"></h3>
        </div>
      </div>
    </v-overlay>
    <OrderTemplate :step="1" :steps="steps" v-if="isReady">
      <template v-slot:stepCard>
        <div
          class="text-center bg-tranparent-yellow pa-3 text-title font-weight-bold rwd-margin is-word-break"
          style="border-radius: 0"
        >
          {{
            !!session.ticketArea
              ? $t("order_step_1_ticketarea_title")
              : $t("order_step_1_product_title")
          }}
        </div>
        <div v-if="isLoading" class="text-center py-10">
          <v-sheet width="100%" height="200" class="text-center pt-15">
            <v-progress-circular indeterminate color="grey" size="48" />
          </v-sheet>
        </div>
        <template v-else>
          <NonSeat
            v-if="!session.ticketArea"
            @update="updateTicket"
            :refresh="isRefresh"
            :clear="doClear"
            :reselect="reselect"
            @refreshEnd="refreshTicket"
          />
          <SeatArea
            v-else-if="session.ticketArea && !session.groupBy"
            @update="updateTicket"
            :refresh="isRefresh"
            :clear="doClear"
            :reselect="reselect"
            @refreshEnd="refreshTicket"
          />
          <GroupSeat
            v-else
            @update="updateTicket"
            :refresh="isRefresh"
            :clear="doClear"
            :reselect="reselect"
            @refreshEnd="refreshTicket"
          />
        </template>
        <div
          class="exclusive-code"
          ref="exclusiveCode"
          v-if="hasSerial && transactionValidType"
        >
          <v-form ref="form" lazy-validation>
            <div
              class="label"
              v-html="
                !!hint
                  ? hint
                  : SNDescription
                  ? SNDescription.title
                  : $t('order_step_1_serial_code_required')
              "
            ></div>
            <v-text-field
              v-model="serialNumber"
              :placeholder="
                !!hint
                  ? $t('order_step_1_serial_ans_field')
                  : !!SNDescription
                  ? SNDescription.description
                  : $t('order_step_1_serial_code_field')
              "
              label=""
              :error-messages="
                serialNumberErr && serialNumberErr !== ''
                  ? [serialNumberErr]
                  : null
              "
              filled
              outlined
            ></v-text-field>
          </v-form>
        </div>
      </template>
      <!-- Footer -->
      <template v-if="!session.ticketArea" v-slot:orderFooter>
        <v-spacer />
        <v-col cols="4" sm="3">
          <v-btn
            class="nextBtn"
            :elevation="0"
            :class="{ disabledBtn: !canNextStep }"
            :disabled="!canNextStep"
            @click="beforeNext"
            block
            depressed
            :large="$vuetify.breakpoint.xs"
            :x-large="!$vuetify.breakpoint.xs"
            >{{ $t("next_step") }}</v-btn
          >
        </v-col>
      </template>
      <template v-else v-slot:orderFooter>
        <v-col
          cols="12"
          sm="6"
          md="4"
          :class="'d-flex align-center align-content-center pb-3'"
        >
          <v-radio-group
            row
            class="ma-0 pa-0 radioBtns"
            v-model="seatReserve"
            :disabled="
              (selectedArea && selectedArea.virtualSeat) ||
                !seatAssignmentInProd ||
                !selectedTickets.length
            "
          >
            <div
              class="radio-item-group"
              v-if="
                !session.pickSeatOption || Number(session.pickSeatOption) !== 2
              "
            >
              <v-radio
                :label="$t('order_step_1_seat_reserved_field')"
                color="primary"
                value="1"
              >
              </v-radio>
            </div>
            <template
              v-if="
                selectedArea &&
                  !selectedArea.virtualSeat &&
                  (!session.pickSeatOption ||
                    Number(session.pickSeatOption) !== 1)
              "
            >
              <v-radio
                :label="$t('order_step_1_seat_customize_field')"
                color="primary"
                :class="session.pickSeatOption != 2 ? 'ml-md-2' : ''"
                value="2"
              ></v-radio>
            </template>
          </v-radio-group>
        </v-col>
        <v-spacer />
        <v-col cols="12" sm="6" :align-self="'end'">
          <v-row :justify="'end'">
            <v-col cols="6" sm="6" class="py-0">
              <v-btn
                class="font-weight-bold"
                color="primary"
                outlined
                style="border: 2px solid"
                :class="{ disabledBtn: !totalTicket }"
                :disabled="!totalTicket"
                @click="clearSelect()"
                block
                :x-large="!$vuetify.breakpoint.xs"
                >{{ $t("order_step_1_clear_selection") }}</v-btn
              >
            </v-col>
            <v-col cols="6" sm="6" class="py-0">
              <v-btn
                class="nextBtn"
                :elevation="0"
                :class="{ disabledBtn: !canNextStep }"
                :disabled="!canNextStep"
                @click="beforeNext"
                block
                :x-large="!$vuetify.breakpoint.xs"
                >{{ $t("next_step") }}</v-btn
              >
            </v-col>
          </v-row>
        </v-col>
      </template>
    </OrderTemplate>
    <v-btn
      v-if="!isPending"
      rounded
      color="accent-blue"
      class="float-btn white--text"
      elevation="8"
      @click="isRefresh = true"
    >
      <template v-if="isRefresh">
        <v-progress-circular
          indeterminate
          color="grey-1"
          size="25"
          width="2"
        ></v-progress-circular>
      </template>
      <template v-else>
        {{ $t("order_step_1_refresh_ticket") }}
        <v-icon>mdi-refresh</v-icon>
      </template>
    </v-btn>
  </div>
</template>

<script>
import OrderTemplate from "./OrderTemplate.vue";
import NonSeat from "./components/NonSeat.vue";
import SeatArea from "./components/SeatArea.vue";
import GroupSeat from "./components/GroupSeat.vue";
import { mapActions, mapGetters } from "vuex";
import { getEvent, getSessions } from "@/api/static";
import jwt_decode from "jwt-decode";
import { CurrentReservedMixins } from "@/shared/mixins";
import CC from "@/api/config-center";
import TM from "@/api/ticket-management";
import { VFabTransition as VFab } from "vuetify/lib/components";

const disabilityEnable =
  process.env["VUE_APP_DISABILITY_ENABLE"] === true ||
  process.env["VUE_APP_DISABILITY_ENABLE"] === "true";

window.isEnquene = false;

export default {
  mixins: [CurrentReservedMixins],
  components: {
    OrderTemplate,
    NonSeat,
    SeatArea,
    GroupSeat,
    VFab
  },
  metaInfo() {
    return {
      title: `${
        !this.session.ticketArea
          ? this.$t("order_step_1_product_title")
          : this.$t("order_step_1_ticketarea_title")
      } | ${this.session.name || ""} | ${this.$t("app_name")}`,
      script: [
        {
          type: "application/ld+json",
          json: {
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: `${
              !this.session.ticketArea
                ? this.$t("order_step_1_product_title")
                : this.$t("order_step_1_ticketarea_title")
            } | ${this.session.name || ""} | ${this.$t("app_name")}
          `,
            url: window.location.href
          }
        }
      ]
    };
  },
  data() {
    return {
      abortController: new AbortController(),
      disabilityEnable: disabilityEnable,
      isDisabilityValid: true,
      reserveTimer: null,
      pending: 0,
      defaultWaitSec: 15,
      isRefresh: false,
      hasOrder: false,
      isReady: false,
      isPending: false,
      isTrigger: false,
      hasArea: false,
      reselect: false,
      selectedTickets: [],
      selectedArea: [],
      totalTicket: 0,
      isScollEnd: false,
      isSubmit: false,
      doClear: false,
      isLoading: true,
      loadingData: true,
      hasSerial: false,
      seatAssignmentInProd: true,
      serialNumber: "",
      serialNumberErr: null,
      seatReserve: "1",
      consecutiveSeats: false,
      disability: null,
      pendingTimer: null,
      currentEnquneRes: null
    };
  },
  computed: {
    ...mapGetters({
      areaAssignment: "order/areaAssignment",
      isPopState: "order/isPopState",
      campaign: "event/campaign",
      isPopState: "order/isPopState",
      session: "session/sessionInfo",
      productData: "session/productData",
      couldCulturePoint: "culturePoint/couldCulturePoint"
    }),
    pendingWording() {
      const wordings = [
        this.$t(
          `reserve_waiting_1${this.campaign.isLottery ? "_lottery" : ""}`
        ),
        this.$t(`reserve_waiting_2${this.campaign.isLottery ? "_lottery" : ""}`)
      ];

      if (this.isPending) {
        this.pendingTimer = setTimeout(() => {
          this.pending++;
          if (this.pending === 2) {
            this.pending = 0;
          }
          clearTimeout(this.pendingTimer);
        }, 3000);
      }

      return wordings[this.pending];
    },
    transactionValidType() {
      return (
        !!this.session.transactionValidType &&
        this.session.transactionValidType !== ""
      );
    },
    canNextStep() {
      return (
        this.selectedTickets.length &&
        (!this.disabilityEnable ||
          (this.disabilityEnable && this.isDisabilityValid))
      );
    },
    SNDescription() {
      if (!this.selectedTickets.length) return null;
      return this.selectedTickets.reduce((accr, ticket) => {
        if (
          !accr &&
          ticket.serialKey &&
          this.sessionInfo &&
          this.sessionInfo.SNDescription &&
          this.sessionInfo.SNDescription[ticket.serialKey]
        ) {
          return this.sessionInfo.SNDescription[ticket.serialKey][
            this.$store.getters.locale
          ];
        } else {
          return null;
        }
      }, null);
    },
    hint() {
      let ticket = this.selectedTickets.find(t => !!t.hint);
      return !!ticket ? ticket.hint : null;
    },
    steps() {
      return !this.areaAssignment
        ? [
            this.$t("order_step_1"),
            this.$t("order_step_2"),
            this.$t("order_step_3")
          ]
        : [
            this.$t(
              `order_seat_step_1${this.campaign.isLottery ? "_lottery" : ""}`
            ),
            this.$t(
              `order_seat_step_2${this.campaign.isLottery ? "_lottery" : ""}`
            ),
            this.$t("order_seat_step_3"),
            this.$t(
              `order_seat_step_4${this.campaign.isLottery ? "_lottery" : ""}`
            )
          ];
    }
  },
  watch: {
    isPopState: {
      deep: true,
      handler: async function(newVal, old) {
        if (newVal !== old && !newVal) {
          this.$store.dispatch("setOverlayLoading", true);
          this.isReady = true;
          this.resetOrderState();
          this.setPageData(null);
          this.setDisability();
          this.init();
        }
      }
    }
  },
  methods: {
    ...mapActions({
      buyTickets: "order/buyTickets",
      setAreaAssignment: "order/setAreaAssignment",
      setSeatAssignment: "order/setSeatAssignment",
      resetOrderState: "order/resetOrderState",
      setSelectedArea: "order/setSelectedArea",
      setTicketProduct: "session/setTicketProduct",
      showDialog: "dialog/show",
      showLogIn: "login/show",
      logout: "user/logout",
      setCampaign: "event/setCampaign",
      setSession: "session/setSession",
      setTimer: "order/setTimer",
      canCulturePoint: "culturePoint/canCulturePoint"
    }),
    clearSelect() {
      this.doClear = true;
    },
    async getSessionInfo(sessionData, sessionInfo) {
      this.hasArea = sessionInfo[0].ticketArea;
      this.setSession({
        ...sessionData,
        ...sessionInfo[0]
      });

      if (this.session.pickSeatOption && this.session.pickSeatOption === 2) {
        this.seatReserve = "2";
      }
      this.setAreaAssignment(this.session.ticketArea);
      this.isLoading = false;
    },
    async init() {
      this.totalTicket = 0;
      const { data: eventsData } = await getEvent(this.$route.params.eventId);
      const { data: sessionData } = await getSessions(
        this.$route.params.eventId
      );

      let [{ data: infoData }, getInfosErr] = await CC.getInfos({
        eventId: [this.$route.params.eventId],
        sessionId: [this.$route.params.sessionId]
      });
      if (infoData.errCode == "00") {
        // await this.genarateCaptcha();
        this.resetOrderState();
        this.setCampaign({ ...eventsData, ...infoData.result.event[0] });
        this.canCulturePoint(this.campaign.culturePoint || false);
        await this.getSessionInfo(
          sessionData.sessions.find(({ sessionId }) => {
            let sid = /^s[0-9]{9}$/.test(sessionId)
              ? encrypt(sessionId)
              : sessionId;
            return sid == this.$route.params.sessionId;
          }),
          infoData.result.session
        );
        this.$store.dispatch("setOverlayLoading", false);
      } else {
        this.$store.dispatch("setOverlayLoading", false);
        this.showDialog({
          alert: true,
          content: `<div class="pa-6 text-center font-weight-bold">
          <img
            src="/image/ic-warning@3x.png"
            alt=""
            style="width:80px"
          />
          <div class="mt-5">${this.$t("order_step_1_loading_error")}</div>
        </div>`
        });
      }

      this.$gtm.trackView(
        `${
          !this.session.ticketArea
            ? this.$t("order_step_1")
            : this.$t("order_seat_step_1")
        }-${this.session.name}`,
        this.$route.path
      );
    },
    async updateInfo() {
      const { data: eventsData } = await getEvent(this.$route.params.eventId);
      const { data: sessionData } = await getSessions(
        this.$route.params.eventId
      );

      let [{ data: infoData }, getInfosErr] = await CC.getInfos({
        eventId: [this.$route.params.eventId],
        sessionId: [this.$route.params.sessionId]
      });

      if (infoData.errCode == "00") {
        this.canCulturePoint(this.campaign.culturePoint || false);
        this.setCampaign({ ...eventsData, ...infoData.result.event[0] });
        await this.getSessionInfo(
          sessionData.sessions.find(({ sessionId }) => {
            let sid = /^s[0-9]{9}$/.test(sessionId)
              ? encrypt(sessionId)
              : sessionId;
            return sid == this.$route.params.sessionId;
          }),
          infoData.result.session
        );
      }
    },
    gotoActivity() {
      e.preventDefault();
      if (this.$route.name !== "Activity") {
        this.$router
          .push({
            name: "Activity",
            params: { id: this.$route.params.eventId }
          })
          .catch(() => {});
      }
    },
    async beforeNext(event) {
      this.isSubmit = false;
      event.preventDefault();

      if (
        this.hasSerial &&
        this.transactionValidType &&
        this.serialNumber === ""
      ) {
        window.scrollTo({
          left: 0,
          top:
            this.$refs.exclusiveCode.offsetTop +
            this.$refs.exclusiveCode.clientHeight,
          behavior: "smooth"
        });
        return;
      }
      if (this.canNextStep) {
        this.enquene();
      }
    },
    errorHandler(name, reserveRes, reserveData) {
      if (this.$route.name !== "Order1") return;
      if (reserveRes.errCode == "110") {
        setTimeout(() => {
          this.showDialog({
            error: true,
            // title: this.$t('order_step_1_stream_dialog_title'),
            content: this.$t(`order_step_1_stream_dialog_content`),
            button: this.$t("layout_notification_confirm"),
            action: () => {
              this.init();
            }
          });
        }, 1500);
      } else if (reserveRes.errCode == "101") {
        this.showDialog({
          error: true,
          content: this.$t(`order_step_1_reserve_failed`),
          button: this.$t("layout_notification_confirm"),
          action: () => {
            this.init();
          }
        });
        setTimeout(() => {
          this.logout();
          this.showLogIn();
        }, 2000);
      } else if (reserveRes.errCode == "103") {
        this.showDialog({
          alert: true,
          content: `<div class="pa-6 text-center font-weight-bold">
                <img
                  src="/image/ic-warning@3x.png"
                  alt=""
                  style="width:80px"
                />
                <div class="mt-5">${this.$t(
                  "order_step_1_error_dialog_content"
                )}</div>
              </div>`
        });
        setTimeout(() => {
          this.logout();
          this.showLogIn();
        }, 2000);
      } else if (reserveRes.errCode == "111" || reserveRes.errCode == "112") {
        this.showDialog({
          error: true,
          title: this.$t("order_step_1_reserve_dialog_title"),
          content: this.$t("order_step_1_reserve_limit_dialog_content"),
          button: this.$t("order_step_1_reserve_dialog_cta")
        });
        this.$store.dispatch("setOverlayLoading", false);
      } else if (reserveRes.errCode == "115") {
        this.showDialog({
          error: true,
          title: this.$t("order_step_1_reserve_dialog_title"),
          content: this.$t("order_step_1_reserve_limit_dialog_content"),
          button: this.$t("order_step_1_reserve_dialog_cta"),
          action: () => {
            this.$router.push({
              name: "Activity",
              params: {
                id: this.$route.params.eventId
              }
            });
          }
        });
        this.$store.dispatch("setOverlayLoading", false);
      } else if (reserveRes.errCode == "121") {
        this.showDialog({
          error: true,
          title: this.$t("order_step_1_reserve_dialog_title"),
          content: this.$t("order_step_1_reserve_limit_dialog_content"),
          button: this.$t("order_step_1_reserve_dialog_cta"),
          action: () => {
            window.location.reload();
          }
        });
      } else if (reserveRes.errCode == "113") {
        this.showDialog({
          error: true,
          title: this.$t("order_step_1_reserve_dialog_title"),
          content: this.$t("order_step_1_reserve_area_no_seat_content"),
          button: this.$t("order_step_1_reserve_dialog_cta"),
          action: () => {
            window.location.reload();
          }
        });
        this.init();
      } else if (reserveRes.errCode == "114") {
        this.showDialog({
          alert: true,
          content: `<div class="pa-6 text-center font-weight-bold">
                <img
                  src="/image/ic-warning@3x.png"
                  alt=""
                  style="width:80px"
                />
                <div class="mt-5">${this.$t(
                  "order_step_1_reserve_timeout"
                )}</div>
              </div>`
        });
        this.init();
      } else if (reserveRes.errCode == "116") {
        this.showDialog({
          title:
            this.consecutiveSeats !== true
              ? this.$t("order_step_1_no_consecutive_seats_title")
              : this.$t("order_step_1_reserve_dialog_title"),
          content:
            this.consecutiveSeats !== true
              ? this.$t("order_step_1_no_consecutive_seats_content")
              : this.$t("order_step_1_reserve_no_seat_content"),
          buttons: [
            {
              text: this.$t("order_step_1_reselect_cta"),
              style: {
                block: true,
                outlined: true,
                color: "primary"
              },
              action: () => {
                this.reselect = true;
                setTimeout(() => {
                  this.reselect = false;
                }, 1000);
              }
            },
            {
              text: this.$t("order_step_1_yes_cta"),
              style: {
                depressed: true,
                block: true,
                outlined: false,
                color: "primary"
              },
              action: () => {
                if (this.consecutiveSeats === true) {
                  this.init();
                } else {
                  this.consecutiveSeats = true;
                  this.nextStep(reserveData);
                }
              }
            }
          ]
        });
        // this.init();
      } else if (reserveRes.errCode == "124") {
        this.serialNumberErr = this.hint
          ? this.$t("order_step_1_qa_failed")
          : this.$t("order_step_1_serial_code_error");
        this.$store.dispatch("setOverlayLoading", false);
      } else if (reserveRes.errCode == "125") {
        this.serialNumberErr = this.hint
          ? this.$t("order_step_1_qa_failed")
          : this.$t("order_step_1_serial_code_used");
        this.$store.dispatch("setOverlayLoading", false);
      } else if (reserveRes.errCode == "135") {
        this.captchaErr = this.$t("order_step_1_captcha_fail_content");
        this.$store.dispatch("setOverlayLoading", false);
      } else if (reserveRes.errCode == "136") {
        this.captchaErr = this.$t("order_step_1_captcha_expired_message");
        this.$store.dispatch("setOverlayLoading", false);
      } else if (reserveRes.errCode == "137") {
        this.isPending = true;
        this.reserveTimer = setTimeout(() => {
          clearTimeout(this.reserveTimer);
          if (name === "reserve") this.enquene();
          else this.nextStep(reserveData);
        }, (reserveRes.waitSecond || this.defaultWaitSec) * 1000);
      } else {
        this.showDialog({
          alert: true,
          content: `<div class="pa-6 text-center font-weight-bold">
                <img
                  src="/image/ic-warning@3x.png"
                  alt=""
                  style="width:80px"
                />
                <div class="mt-5">${this.$t("order_step_1_error")}</div>
              </div>`
        });
        window.isEnquene = false;
        this.isPending = false;
        this.init();
      }
    },
    async getCurrentReserve() {
      this.$store.dispatch("setOverlayLoading", true);
      // 未完戝訂單
      const {
        hasReservedOrder,
        currentReserved,
        reservedEventId,
        reservedSessionId
      } = await this.checkReservedOrder();

      if (hasReservedOrder) {
        this.showDialog({
          title: this.$t("uncomplete_order"),
          content: this.$t("uncomplete_order_content"),
          buttons: [
            {
              text: this.$t("return_to_outstanding_order"),
              style: {
                block: true,
                outlined: false,
                color: "primary"
              },
              action: async () => {
                this.setTicketProduct({ products: [] });
                this.setSession();
                this.hasOrder = true;
                this.$router
                  .push({
                    name:
                      currentReserved.tickets[0].seats &&
                      currentReserved.tickets[0].seats.length
                        ? "ConfirmSt"
                        : "Order2",
                    params: {
                      eventId: reservedEventId,
                      sessionId: reservedSessionId
                    }
                  })
                  .catch(() => {});
              }
            }
          ]
        });
      }
    },
    async enquene() {
      window.isEnquene = true;
      if (!this.canNextStep) return;
      this.serialNumberErr = "";
      this.captchaErr = "";
      this.isPending = true;

      let prods = this.selectedTickets.map(({ productId, count }) => ({
        productId,
        count
      }));

      let reserveData = {
        products: prods
      };

      this.setSeatAssignment(this.seatAssignmentInProd);

      if (this.session.ticketArea && this.seatAssignmentInProd) {
        reserveData.reserveSeats = true;
        reserveData.consecutiveSeats = false;
        if (this.seatReserve === "1") {
          reserveData.finalizedSeats = true;
        }
      }

      if (this.transactionValidType && this.hasSerial) {
        reserveData.serialNumber = this.serialNumber;
      }

      const { data: reserveRes } = await TM.ticketEnqueue(
        reserveData,
        this.abortController
      );

      if (reserveRes.errCode !== "00") {
        if (!this.isPending) return;
        if (reserveRes.localCheck) {
          this.isRefresh = true;
          this.currentEnquneRes = reserveRes;
        } else {
          if (!!reserveRes.waitSecond) {
            setTimeout(async () => {
              await this.enquene();
            }, reserveRes.waitSecond * 1000);
          } else {
            window.isEnquene = false;
            this.isPending = false;
            this.errorHandler("enqueue", reserveRes);
          }
        }
      } else {
        window.isEnquene = false;
        if (reserveRes.currentReservedOrderId) {
          this.getCurrentReserve();
        } else {
          this.nextStep({
            ...reserveData,
            uuid: reserveRes.uuid
          });
        }
      }
    },
    async nextStep(reserveData) {
      this.isSubmit = false;

      this.serialNumberErr = "";
      this.captchaErr = "";

      const response = await TM.ticketReserve(reserveData);
      const { data: reserveRes } = response;
      this.isSubmit = true;

      if (Number(reserveRes.errCode) === 999) {
        this.showDialog({
          alert: true,
          title: this.$t("activity_busy_title"),
          content: this.$t("activity_busy_content")
        });
        this.isPending = false;
        this.$store.dispatch("setOverlayLoading", false);
        return;
      }

      if (response.status !== 200) {
        this.reserveTimer = setTimeout(() => {
          clearTimeout(this.reserveTimer);
          this.nextStep(reserveData);
        }, (reserveRes.waitSecond || this.defaultWaitSec) * 1000);
        return;
      }
      if (reserveRes.orderId) {
        this.$store.dispatch("setOverlayLoading", true);
        this.isPending = false;
        this.hasOrder = true;
        await this.updateInfo();
        this.setSelectedArea(this.selectedArea);

        let [{ data: ProductInfo }, getInfosErr] = await CC.getInfos({
          productId: reserveRes.products.map(p => p.productId)
        });
        const prods = reserveRes.products.reduce((accr, p, i) => {
          const prod = this.productData.find(
            prod => p.productId === prod.productId
          );
          const info = ProductInfo.result.product.find(
            t => p.productId === t.id
          );
          accr.push({
            ...prod,
            ...info,
            ...p
          });
          return accr;
        }, []);
        const reserveData = {
          ...reserveRes,
          expiryTimestamp: reserveRes.products[0].expiryTimestamp,
          createdAt: reserveRes.products[0].createdAt,
          products: prods
        };
        await this.reserveTicket(reserveData);
        await this.buyTickets(this.selectedTickets);
        // 絝帳_靸擇票種_{活動坝稱}_{場次坝稱}_下一步
        this.$router
          .push({
            name:
              !this.session.ticketArea || !this.seatAssignmentInProd
                ? "Order2"
                : this.seatReserve === "1"
                ? "ConfirmSt"
                : "SelSt",
            params: { sessionId: this.$route.params.sessionId }
          })
          .catch(() => {});
      } else {
        if (reserveRes.errCode !== "137") {
          this.isPending = false;
          this.$store.dispatch("setOverlayLoading", false);
        }
        // todo reserve fail
        this.errorHandler("reserve", reserveRes, reserveData);

        this.$store.dispatch("setOverlayLoading", false);
      }
    },
    needDisbilityWarning(info) {
      let result = false;
      let today = new Date().valueOf();
      let disabilityVaideDate =
        this.disability && this.disability.validDate
          ? new Date(this.disability.validDate).valueOf()
          : null;
      let dateInValid =
        !disabilityVaideDate ||
        (disabilityVaideDate && disabilityVaideDate < today);

      info.selectedTickets.forEach(ticket => {
        if (
          !(
            !ticket.disabilitiesType &&
            typeof ticket.disabilitiesType !== "number"
          )
        ) {
          let disabilityCheck =
            !this.disability || (this.disability && !this.disability.isCrip);
          switch (ticket.disabilitiesType) {
            case 1:
              result = disabilityCheck || dateInValid;
              break;
            case 2:
              result =
                disabilityCheck || !this.disability.playNeed || dateInValid;
              break;
            case 3:
              result =
                disabilityCheck ||
                !this.disability.wheelchairNeed ||
                dateInValid;
              break;
          }
        }
      });

      return result;
    },
    updateTicket(info) {
      this.isDisabilityValid = true;
      if (this.disabilityEnable && this.needDisbilityWarning(info)) {
        this.isDisabilityValid = false;
        this.showDialog({
          title: this.$t("reserver_disability_invalid_title"),
          content: this.$t("reserver_disability_invalid_content"),
          buttons: [
            {
              text: this.$t("layout_notification_cancel"),
              style: {
                block: true,
                outlined: true,
                color: "primary"
              },
              action: () => {
                this.clearSelect();
              }
            },
            {
              text: this.$t("reserver_disability_invalid_go"),
              style: {
                depressed: true,
                block: true,
                outlined: false,
                color: "primary"
              },
              action: () => {
                this.$store.dispatch("setOverlayLoading", true);
                this.$router
                  .push({
                    name: "UserInfo",
                    params: {
                      urlTabSelect: 1,
                      needDisbilityVerify: true
                    }
                  })
                  .catch(() => {});
              }
            }
          ]
        });
      }

      this.hasSerial = false;
      this.selectedTickets = info.selectedTickets;
      if (info.selectedArea) this.selectedArea = info.selectedArea;
      this.totalTicket = info.totalTicket;
      this.seatAssignmentInProd = info.seatAssignmentInProd;

      if (this.doClear && !info.totalTicket) {
        this.doClear = false;
      }
      if (this.selectedTickets.filter(t => !!t.serialKey).length) {
        this.hasSerial = true;
      }
    },
    async refreshTicket() {
      this.isRefresh = false;
      let productEmpty = false;
      await this.updateInfo();
      this.selectedTickets.forEach(t => {
        if ((t.productLimit && !t.remain) || t.status !== "onsale")
          productEmpty = true;
      });

      if (!window.isEnquene) return;
      if (
        (this.session.sessionLimit && !this.session.count) ||
        (this.selectedArea.ticketAreaLimit && !this.selectedArea.count) ||
        productEmpty
      ) {
        window.isEnquene = false;
        this.errorHandler("refresh", { errCode: "121" });
        return;
      }
      if (this.currentEnquneRes && this.currentEnquneRes.waitSecond) {
        setTimeout(async () => {
          await this.enquene();
        }, this.currentEnquneRes.waitSecond * 1000);
      }
    },
    setDisability() {
      const user = JSON.parse(Cookies.get("user"));
      const token = jwt_decode(user.access_token);
      this.disability = token.disability || null;
    }
  },
  create() {
    localStorage.removeItem("orderTimeout");
    this.$store.dispatch("setOverlayLoading", true);
  },
  async mounted() {
    if (!this.isPopState) {
      this.isReady = true;
      this.resetOrderState();
      this.setPageData(null);
      this.setDisability();

      this.init();
    }
  },
  async beforeRouteLeave(to, from, next) {
    if (this.isPending || this.reserveTimer) {
      this.abortController.abort();
      this.isPending = false;
      clearTimeout(this.reserveTimer);
    }
    if (
      !this.hasOrder &&
      ["Order2", "ConfirmSt", "SelSt", "Order3"].includes(to.name)
    ) {
      next(false);
      this.$router.push({
        name: "Activity",
        params: {
          id: this.$route.params.eventId
        }
      });

      return false;
    }
    next();
  }
};
</script>

<style lang="scss" scoped>
.v-chip.v-size--default {
  height: 40px;
  width: 40px;
  border-radius: 20px;
  margin: 0 20px;
}

::v-deep .v-chip .v-chip__content {
  justify-content: center;
}

@keyframes colorMoving {
  0% {
    left: 0;
  }

  100% {
    left: -100%;
  }
}

::v-deep {
  .disabled-text {
    font-weight: bold;
    color: #c4c4c4;
  }

  .ticket-price {
    line-height: 1;
    margin-top: 6px;
  }

  .captcha-img {
    position: relative;
    overflow: hidden;
  }

  @media screen and (max-width: 600px) {
    .captcha-img svg {
      width: 151px;
    }

    .disabled-text {
      font-size: 16px;
    }
  }

  .count-button {
    max-width: 124px;

    div {
      font-weight: bold;
      font-size: 18px;
    }

    @media screen and (max-width: 600px) {
      max-width: 80px;

      button.v-btn.v-btn--fab.v-size--x-small {
        width: 20px;
        height: 20px;

        i.v-icon {
          font-size: 20px;
        }
      }
    }
  }

  .price-group.v-expansion-panels {
    background-color: none;
    border-radius: 0;
    box-shadow: none;

    > .v-expansion-panel {
      background-color: none;
      border-radius: 0;
      box-shadow: none;
      margin: 0 0 8px;

      @media screen and (max-width: 600px) {
        margin-bottom: 0;
      }

      &::after,
      &::before {
        display: none;
      }
    }

    > .v-expansion-panel > .v-expansion-panel-header {
      background: $grey-lighten-2;
      color: #1d5166;
      font-size: 24px;
      padding: 12px 15px;
      border-radius: 0;

      h4 {
        text-align: center;
      }

      .v-icon {
        color: #757575 !important;
      }
    }

    > .v-expansion-panel
      > .v-expansion-panel-content
      > .v-expansion-panel-content__wrap {
      padding: 8px 0 0;
    }
  }

  @media screen and (min-width: 960px) {
    .area-image {
      position: sticky;
      top: 150px;
    }
  }

  .exclusive-code {
    display: block;
    margin: 15px 15px 0;
    padding: 24px 24px 0;
    background-color: transparentize($primary-dark, 0.8);

    .label {
      font-size: 24px;
      font-weight: bold;
      margin-bottom: 20px;
    }

    .v-text-field.v-text-field--enclosed:not(.v-text-field--rounded) {
      max-width: 560px;

      > .v-input__control > .v-input__slot {
        background-color: white;
      }
    }

    @media screen and (max-width: 600px) {
      margin: 0;
      padding: 15px 15px 0;

      .label {
        font-size: 16px;
        font-weight: bold;
        margin-bottom: 14px;
      }
    }
  }

  .radioBtns,
  .radioBtns .v-input--radio-group__input {
    display: flex;
    justify-content: space-between;

    .v-input.v-input--checkbox {
      padding: 0;
    }

    .v-messages {
      display: none !important;
    }

    @media screen and (max-width: 960px) and (min-width: 601px) {
      flex-direction: column;
    }

    @media screen and (max-width: 600px) {
      width: 100%;

      .v-radio {
        margin-right: 8px;
      }
    }

    .v-input__slot {
      margin-bottom: 0;
    }

    .v-input.v-input--checkbox .v-input__control {
      .v-input__slot {
        margin-bottom: 0;
      }
    }

    .radio-item-group {
      display: flex;
    }
  }

  .v-chip .v-chip__content {
    justify-content: center;
  }

  .area-row-header .row {
    box-sizing: border-box;
    margin-top: 0;
    margin-bottom: 0;
    font-weight: 600;
  }

  .seats-area {
    .v-expansion-panel {
      border-radius: 4px !important;
      border: solid 1px #dedede;
      box-shadow: none;
      margin: 0 0 12px;

      @media screen and (max-width: 600px) {
        margin: 0 0 8px;
      }

      &.is-disabled {
        pointer-events: none;
      }

      .v-expansion-panel-header {
        padding: 16px 15px;
        font-size: 20px;
        line-height: 1;
        font-weight: 500;

        small {
          font-size: 18px;
          color: $accent-red;
        }

        .area-color {
          display: inline-block;
          width: 20px;
          height: 20px;
          margin-right: 8px;
          border-radius: 50%;
          vertical-align: middle;
        }

        .v-expansion-panel-header__icon {
          display: none;
        }
      }

      .v-expansion-panel-content__wrap {
        padding: 0;
        font-size: 18px;

        .row {
          padding: 12px 15px;

          + .row {
            border-top: 1px solid #eaeaea;
          }
        }
      }

      &::before,
      &::after {
        display: none;
      }
    }

    .v-expansion-panel.v-expansion-panel--active .v-expansion-panel-header {
      pointer-events: none;
    }

    .v-expansion-panel.is-disabled,
    &.is-select .v-expansion-panel:not(.v-expansion-panel--active) {
      .v-expansion-panel-header .area-color {
        background-color: #c4c4c4 !important;
      }

      .v-expansion-panel-header *:not(.v-chip) {
        color: #c4c4c4;
      }
      .v-expansion-panel-header .v-chip {
        opacity: 0.4 !important;
      }
    }

    .v-expansion-panel:hover,
    .v-expansion-panel.v-expansion-panel--active {
      border-radius: 20px;
      border-color: transparent;
      box-shadow: 0 5px 10px 5px rgba(0, 0, 0, 0.1);
    }
  }

  .v-btn.nextBtn {
    box-shadow: none;
  }

  @media screen and (max-width: 600px) {
    .v-card.has-ticketarea {
      margin: 0 -12px;
      width: 100vw;
      max-width: 100vw;
    }

    .price-group.v-expansion-panels > .v-expansion-panel {
      margin-bottom: 8px;

      > .v-expansion-panel-header {
        min-height: 40px;
        padding: 5px 15px;

        h4 {
          font-size: 16px !important;
        }
      }
    }

    .seats-area .v-expansion-panel {
      border-radius: $radiusMobile !important;

      .v-expansion-panel-header {
        font-size: 16px;
        padding: 5px 15px;
        min-height: 38px;

        h4 {
          font-size: 16px !important;
        }

        *:not(.text-small):not(small):not(.v-chip):not(.v-chip__content) {
          font-size: 16px !important;
        }

        small {
          font-size: 12px;
          margin-left: 4px;
        }

        .v-icon {
          color: #757575;
        }

        .row .col {
          display: flex;
          align-items: center;
          align-content: center;
          white-space: nowrap;

          &.text-right {
            justify-content: flex-end;
          }
        }

        .area-color {
          width: 24px;
          height: 24px;
        }

        small {
          font-size: 0.8rem;
        }
      }

      .v-expansion-panel-content__wrap {
        padding: 0 15px 12px 15px;

        .row {
          padding: 8px 0;
        }
      }
    }
  }
}

// xs and sm
@media screen and (max-width: 960px) {
  .rwd-margin {
    margin: 0 16px;
  }
}

// greater than sm
@media only screen and (min-width: 960px) {
  .rwd-margin {
    margin: 0 32px;
  }
}

::v-deep {
  .soldout,
  .soldout *,
  .soldout .red--text {
    color: $grey-1 !important;
    pointer-events: none;

    .count-button {
      .v-btn {
        color: #bdbdbd !important;
        background-color: rgba(0, 0, 0, 0.12) !important;
      }
    }
  }

  .order-footer .v-btn,
  .order-footer .v-btn.v-btn--outlined:not(.v-btn--disabled) {
    min-width: 150px;
    padding-left: 12px;
    padding-right: 12px;
  }
}
::v-deep {
  .v-btn.float-btn {
    position: fixed !important;
    left: 0;
    right: 0;
    margin: 0 auto;
    padding: 0 !important;
    bottom: 96px;
    width: 120px;
    z-index: 199 !important;

    .v-btn__content {
      display: flex;
      letter-spacing: 0px;
      .v-icon {
        margin-left: 4px;
      }
    }
  }
}
</style>
